import type { IColumn } from '@fluentui/react';
import { ListCell } from '@h2oai/ui-kit';

import { onRenderDisplayName } from '../../components/AdminSettings/Entity/utils';
import { onRenderBooleanCell, onRenderDataCell } from '../../utils/render';
import { DAIEngineProfile_ConfigEditability } from '../gen/ai/h2o/engine/v1/dai_engine_profile_pb';
import { ConstraintType } from '../types';
import { EngineType, EntityActionTypeMSWRestHTTPMethod, EntityField } from './types';
import { getOptionsFromEnum, onRender, onRenderDisplayAndId } from './utils';

const required = true;

export enum EntityType {
  DAIProfile = 'DAIProfile',
  InternalDAIVersion = 'InternalDAIVersion',
  InternalH2OVersion = 'InternalH2OVersion',
  DefaultDAISetup = 'DefaultDAISetup',
  DefaultH2OSetup = 'DefaultH2OSetup',
  DAIEngineProfile = 'DAIEngineProfile',
  H2OEngineProfile = 'H2OEngineProfile',
  NotebookEngineProfile = 'NotebookEngineProfile',
  SandboxEngineTemplate = 'SandboxEngineTemplate',
  DAIEngineVersion = 'DAIEngineVersion',
  H2OEngineVersion = 'H2OEngineVersion',
}

export enum EntityActionType {
  Create = 'Create',
  Get = 'Get',
  List = 'List',
  Update = 'Update',
  Delete = 'Delete',
  UnDelete = 'UnDelete',
  Reorder = 'Reorder',
  Assign = 'Assign',
  Reveal = 'Reveal',
}

export enum EntityFieldType {
  Text = 'Text',
  Number = 'Number',
  UnlimitedNumber = 'UnlimitedNumber',
  Bytes = 'Bytes',
  Boolean = 'Boolean',
  SelectEnum = 'SelectEnum',
  SelectAsync = 'SelectAsync',
  StringArray = 'StringArray',
  ReadOnlyStringArray = 'ReadOnlyStringArray',
  ReadOnly = 'ReadOnly',
  Order = 'Order',
  KeyValuePair = 'KeyValuePair',
  Hidden = 'Hidden',
  Semver = 'Semver',
  NameId = 'NameId',
  LatestAndAliases = 'LatestAndAliases',
  DisplayOnDisplayAndId = 'DisplayOnDisplayAndId',
  IdOnDisplayAndId = 'IdOnDisplayAndId',
  ConstraintNumeric = 'ConstraintNumeric',
  ConstraintDuration = 'ConstraintDuration',
  Duration = 'Duration',
  Yaml = 'Yaml',
}

export type EngineProfileConfig<EngineProfile> = Record<string, EntityField<EngineProfile>>;

export function engineProfileFieldsConfig<EngineProfile>(engineType: EngineType): EngineProfileConfig<EngineProfile> {
  return {
    name: {
      name: 'name' as keyof EngineProfile,
      type: EntityFieldType.IdOnDisplayAndId,
      label: 'Name',
      description: `${engineType} Engine Profile resource name.`,
      required,
    },
    displayName: {
      name: 'displayName' as keyof EngineProfile,
      type: EntityFieldType.DisplayOnDisplayAndId,
      label: 'Display Name',
      description: `Human-readable name of ${engineType} Engine Profile.`,
      required,
    },
    enabled: {
      name: 'enabled' as keyof EngineProfile,
      type: EntityFieldType.Boolean,
      label: 'Enabled',
      description: `Enabled profiles can be used with engines.`,
    },
    priority: {
      name: 'priority' as keyof EngineProfile,
      type: EntityFieldType.Number,
      label: 'Priority',
      description: 'Determines ordering of profiles. A lower value means higher priority.',
    },
    assignedOidcRolesEnabled: {
      name: 'assignedOidcRolesEnabled' as keyof EngineProfile,
      type: EntityFieldType.Boolean,
      label: 'Enforce OIDC Roles',
      description: 'When enabled, only specified OIDC roles have access to this profile.',
    },
    assignedOidcRoles: {
      name: 'assignedOidcRoles' as keyof EngineProfile,
      type: EntityFieldType.StringArray,
      label: 'Assigned OIDC Roles',
      description: 'List of OIDC roles that have access to this profile.',
    },
    cpuConstraint: {
      name: 'cpuConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintNumeric,
      label: 'CPU Constraint',
      description: `Constraint for engine's CPU Units.`,
      constraintType: ConstraintType.CPU,
      minimum: 1,
    },
    gpuConstraint: {
      name: 'gpuConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintNumeric,
      label: 'GPU Constraint',
      description: `Constraint for engine's GPU Units.`,
      constraintType: ConstraintType.GPU,
    },
    nodeCountConstraint: {
      name: 'nodeCountConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintNumeric,
      label: 'Node Count Constraint',
      description: `Constraint for each ${engineType}Engine's node count that uses this profile.`,
      constraintType: ConstraintType.NODECOUNT,
    },
    memoryBytesConstraint: {
      name: 'memoryBytesConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintNumeric,
      label: 'Memory Constraint',
      description: `Constraint for engine's memory.`,
      constraintType: ConstraintType.MEMORYBYTES,
    },
    storageBytesConstraint: {
      name: 'storageBytesConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintNumeric,
      label: 'Storage Constraint',
      description: `Constraint for engine's storage.`,
      constraintType: ConstraintType.STORAGEBYTES,
    },
    maxIdleDurationConstraint: {
      name: 'maxIdleDurationConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintDuration,
      label: 'Max Idle Duration Constraint',
      description: `Constraint for engine's Max Idle Time in hours.`,
      constraintType: ConstraintType.MAXIDLEDURATION,
    },
    maxRunningDurationConstraint: {
      name: 'maxRunningDurationConstraint' as keyof EngineProfile,
      type: EntityFieldType.ConstraintDuration,
      label: 'Max Running Duration Constraint',
      description: `Constraint for engine's Max Up Time in hours.`,
      constraintType: ConstraintType.MAXRUNNINGDURATION,
    },
    maxNonInteractionDuration: {
      name: 'maxNonInteractionDuration' as keyof EngineProfile,
      type: EntityFieldType.Duration,
      label: 'Max Non Interaction Duration',
      description: `Maximum duration without any user activity before the engine is considered idle.`,
    },
    maxUnusedDuration: {
      name: 'maxUnusedDuration' as keyof EngineProfile,
      type: EntityFieldType.Duration,
      label: 'Max Unused Duration',
      description: `
    Maximum duration an engine can be paused before it is DELETED to save resources.
    Use with caution. Deleted engines cannot be recovered.
    `,
    },
    maxRunningEngines: {
      name: 'maxRunningEngines' as keyof EngineProfile,
      type: EntityFieldType.UnlimitedNumber,
      label: 'Max Running Engines',
      description: 'Maximum number of engines per user that can be running using this profile.',
      minimum: 1,
    },
    tritonEnabled: {
      name: 'tritonEnabled' as keyof EngineProfile,
      type: EntityFieldType.Boolean,
      label: 'Triton Enabled',
      description: "Enables DAI's built-in Triton inference server.",
    },
    configEditability: {
      name: 'configEditability' as keyof EngineProfile,
      type: EntityFieldType.SelectEnum,
      label: `${engineType} Config Editability`,
      description: "Specifies the user's ability to modify the engine configuration.",
      options: getOptionsFromEnum(DAIEngineProfile_ConfigEditability, 'CONFIG_EDITABILITY_', [
        DAIEngineProfile_ConfigEditability.UNSPECIFIED,
      ]),
      selectedOption: DAIEngineProfile_ConfigEditability.FULL,
    },
    baseConfiguration: {
      name: 'baseConfiguration' as keyof EngineProfile,
      type: EntityFieldType.KeyValuePair,
      label: `Base ${engineType} Configuration`,
      description: `Base configuration is the initial configuration before user's configuration or overrides are considered.`,
    },
    configurationOverride: {
      name: 'configurationOverride' as keyof EngineProfile,
      type: EntityFieldType.KeyValuePair,
      label: `${engineType} Configuration Override`,
      description: `Configuration override is applied on top of all other configurations.`,
    },
    yamlPodTemplateSpec: {
      name: 'yamlPodTemplateSpec' as keyof EngineProfile,
      type: EntityFieldType.Yaml,
      label: 'Custom Pod Template (YAML)',
      description: `Custom pod template specification in the YAML format that will be merged with the generated Pod specification.`,
    },
    yamlGpuTolerations: {
      name: 'yamlGpuTolerations' as keyof EngineProfile,
      type: EntityFieldType.Yaml,
      label: 'Custom GPU Tolerations (YAML)',
      description: `YAML representation of custom Pod toleration when the engine has GPU Units > 0.`,
    },
    gpuResourceName: {
      name: 'gpuResourceName' as keyof EngineProfile,
      type: EntityFieldType.Text,
      label: 'GPU Resource Name',
      description: 'Name of the Kubernetes resource name for GPUs.',
      inputDefaultValue: 'nvidia.com/gpu',
    },
    dataDirectoryStorageClass: {
      // DAIEngineProfile and H2OEngineProfile are both using for the same field different name dataDirectoryStorageClass (historical naming)
      name: 'dataDirectoryStorageClass' as keyof EngineProfile,
      type: EntityFieldType.Text,
      label: 'Storage Class Name',
      description: `
      Name of the Kubernetes StorageClass to be used. 
      Defaults to the default storage class of the Kubernetes cluster.
    `,
    },
    storageClassName: {
      name: 'storageClassName' as keyof EngineProfile,
      type: EntityFieldType.Text,
      label: 'Storage Class Name',
      description: `
      Name of the Kubernetes StorageClass to be used. 
      Defaults to the default storage class of the Kubernetes cluster.
    `,
    },
  } as EngineProfileConfig<EngineProfile>;
}

export function engineProfileBaseFields<EngineProfile>(engineType: EngineType): EntityField<EngineProfile>[] {
  return [
    engineProfileFieldsConfig(engineType).name,
    engineProfileFieldsConfig(engineType).displayName,
    engineProfileFieldsConfig(engineType).enabled,
    engineProfileFieldsConfig(engineType).priority,
    engineProfileFieldsConfig(engineType).assignedOidcRolesEnabled,
    engineProfileFieldsConfig(engineType).assignedOidcRoles,
    engineProfileFieldsConfig(engineType).cpuConstraint,
    engineProfileFieldsConfig(engineType).gpuConstraint,
    engineProfileFieldsConfig(engineType).memoryBytesConstraint,
  ];
}

export const entityActionTypeHTTPMethod: EntityActionTypeMSWRestHTTPMethod = {
  [EntityActionType.Create]: 'post',
  [EntityActionType.Get]: 'get',
  [EntityActionType.List]: 'get',
  [EntityActionType.Update]: 'patch',
  [EntityActionType.Delete]: 'delete',
  [EntityActionType.UnDelete]: 'post',
  [EntityActionType.Reorder]: 'post',
  [EntityActionType.Assign]: 'post',
  [EntityActionType.Reveal]: 'get',
};

export const engineImageColumns: IColumn[] = [
  {
    key: 'name',
    name: 'Name',
    fieldName: 'displayName',
    minWidth: 200,
    maxWidth: 350,
    onRender: onRenderDisplayName,
  },
  {
    key: 'enabled',
    name: 'Enabled',
    fieldName: 'enabled',
    minWidth: 80,
    maxWidth: 120,
    onRender: onRenderBooleanCell,
  },
  {
    key: 'image',
    name: 'Image',
    fieldName: 'image',
    minWidth: 350,
    onRender: onRenderDataCell,
  },
  {
    key: 'actions',
    name: 'Actions',
    fieldName: 'actions',
    minWidth: 170,
    maxWidth: 170,
  },
];

export const engineProfilesColumns: IColumn[] = [
  {
    key: 'icon',
    name: 'Icon',
    minWidth: 50,
    maxWidth: 50,
    onRender: () => <ListCell styles={{ root: { paddingLeft: 12 } }} iconProps={{ iconName: 'Settings' }} />,
  },
  {
    key: 'displayName',
    name: 'Display Name',
    fieldName: 'displayName',
    minWidth: 200,
    maxWidth: 250,
    onRender: onRenderDisplayAndId,
  },
  {
    key: 'priority',
    name: 'Priority',
    fieldName: 'priority',
    minWidth: 80,
    maxWidth: 120,
    onRender,
  },
  {
    key: 'enabled',
    name: 'Enabled',
    fieldName: 'enabled',
    minWidth: 80,
    maxWidth: 120,
    onRender: onRenderBooleanCell,
  },
  {
    key: 'actions',
    name: 'Actions',
    fieldName: 'actions',
    minWidth: 170,
    maxWidth: 170,
  },
];
